import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from 'core/icons/chevronRight';
import PrinterIcon from 'core/icons/printer';
import ToolTip from 'core/components/Tooltip';
import {
  useSelectableContext,
} from 'core/hooks';
import {
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import IconButton from '@mui/material/IconButton';
import {
  formatDateTime,
} from 'utils/date';
import {
  formatMoney,
} from '@populateui/utils';
import clsx from 'clsx';
import {
  usePrintMediaRequest,
} from 'views/EMR/hooks/media';
import {
  getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
} from 'endpoints';
import {
  DeliveredPatientStatementDTO,
} from 'dtos/deliveredPatientStatementDTO';

type SentOnCellProps = {
  original: DeliveredPatientStatementDTO;
};

function SentOnCell({ original }: SentOnCellProps) {
  const { isSelected, toggle } = useSelectableContext();
  const expandable = (original.appointmentCount ?? 0) > 0;
  const expanded = isSelected(original?.patientStatementDeliveryLogId ?? '');
  const Icon = expanded ? ExpandMoreIcon : ChevronRightIcon;
  let tooltip = 'No Encounters linked for old statements';

  if (expandable) {
    tooltip = expanded ? 'Collapse' : 'Expand';
  }

  return (
    <div className="flex items-center gap-2">
      <ToolTip title={tooltip}>
        <div className={clsx({ '!cursor-not-allowed': !expandable })}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              toggle(original?.patientStatementDeliveryLogId ?? '');
            }}
            className={clsx('!p-0', { 'opacity-30': !expandable })}
            disableRipple={!expandable}
            disabled={!expandable}
          >
            <Icon className="!text-gray-500" />
          </IconButton>
        </div>
      </ToolTip>
      <div className="flex flex-col">
        <span className="font-semibold">{formatDateTime(original.sentOn ?? '')}</span>
        <span className="italic">
          via
          {' '}
          {original?.type ?? ''}
        </span>
      </div>
    </div>
  );
}

type PrintCellProps = {
  original: DeliveredPatientStatementDTO;
};

function PrintCell({ original }: PrintCellProps) {
  const { print, isPrinting } = usePrintMediaRequest<[number]>(
    getApiMultimediaGetPatientMultiMediaByIdPatientMultiMediaId,
  );
  const hasStatement = (original.patientStatementId ?? 0) > 0;

  return (
    <div className="flex justify-end items-center w-full">
      <ToolTip
        title={hasStatement ? 'Print Statement' : 'Statement is available for printing in Multimedia'}
      >
        <div>
          <IconButton
            size="small"
            disabled={isPrinting || !hasStatement}
            onClick={() => { print([original.patientStatementId ?? 0]); }}
            className={clsx({ '!cursor-not-allowed !pointer-events-auto': !hasStatement })}
          >
            <PrinterIcon className="!w-4 !h-4 !text-inherit" />
          </IconButton>
        </div>
      </ToolTip>
    </div>
  );
}

export default [{
  id: 'sentOn',
  columnTitle: 'Sent on',
  meta: { autoSized: true },
  cell: ({ row: { original } }) => <SentOnCell original={original} />,
}, {
  id: 'due',
  columnTitle: 'Due',
  size: 100,
  cell: ({ row }) => (
    <span className="font-semibold">{formatMoney(row.original.due ?? 0)}</span>
  ),
}, {
  id: 'print',
  size: 32,
  cell: ({ row: { original } }) => <PrintCell original={original} />,
}] as ColumnDef<DeliveredPatientStatementDTO>[];
